import styles from '../../../MagicUpload/components/FilteredHeader/FilteredHeader.module.scss';
import { useEffect, useRef, useState, useCallback } from 'react';
import LoadingSpinner from '../../../_custom/LoadingSpinner/LoadingSpinner';
import CaretIcon from '../../../../assets/Svg/CaretIcon';
import { CHIP_MEASURE_ATTR, useAutoFitChips } from '../../../_custom/Hooks/useAutoFitChips';
import clsx from 'clsx';

type MultiSelectSearchDropdownProps = {
    options: { label: string; value: string | null }[];
    selectedItems: (string | null)[];
    toggleSelection?: (option: (string | null)[]) => void;
    placeholder?: string;
    wrapperStyle?: React.CSSProperties;
    openAbove?: boolean; // Control dropdown position (above or below trigger)
    isSingleSelect?: boolean; // Enable single select mode (vs multi-select)
    onOptionClick?: (option: { label: string; value: string | null }) => void; // Callback for immediate selection (no action buttons)
    disabled?: boolean; // Disable the dropdown
    loading?: boolean; // Show loading spinner
    inputKey?: string; // Unique key for specific dropdown behavior (e.g., 'loanTypes')
    selectedItemStyle?: React.CSSProperties; // Custom inline styles for selected item pill
    selectedItemClassName?: string;
    showFloatingLabel?: boolean; // Show floating label above selected items
    showSelectAll?: boolean; // Show "Select All" checkbox (for multi-select)
    searchBarStyle?: React.CSSProperties; // Custom styles for search bar
    placeholderStyle?: React.CSSProperties; // Custom placeholder text styles
    searchPosition?: 'top' | 'bottom'; // Position of search bar in dropdown
    showRadioButtons?: boolean; // Show radio buttons instead of checkboxes
    pillOptions?: { label: string; value: string | null }[]; // Additional multi-select pill options (tags)
    showActionButtons?: boolean; // Show Back/Confirm buttons for explicit confirmation
    onConfirm?: (selectedOption: string, selectedPills: string[]) => void; // Called when Confirm is clicked
    onCancel?: () => void; // Called when Back is clicked or dropdown closes
    maxVisibleChips?: number | 'auto'; // Maximum number of selected item pills to show before collapsing into "+ N more"
    sortSelectedOnTop?: boolean;
    open?: boolean; // Controlled open state; the component owns it while this is undefined
    onOpenChange?: (open: boolean) => void; // Notified on every open/close, whether controlled or not
};

type PillOptionsSectionProps = {
    pillOptions: { label: string; value: string | null }[];
    selectedPills: string[];
    onPillToggle: (pillValue: string) => void;
};

type ActionButtonsProps = {
    selectedOption: string | null;
    selectedPills: string[];
    onCancel: () => void;
    onConfirm: () => void;
};

// Pill Options Section Component
const PillOptionsSection = ({ pillOptions, selectedPills, onPillToggle }: PillOptionsSectionProps) => {
    return (
        <div className={styles.PillOptionsSection} role="group" aria-label="Tag options">
            <div className={styles.PillsWrapper}>
                {pillOptions.map((pill) => {
                    const isSelected = selectedPills.includes(pill.value as string);
                    return (
                        <button
                            key={pill.value ?? 'not-set'}
                            className={`${styles.PillButton} ${isSelected ? styles.PillButtonSelected : ''}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                onPillToggle(pill.value as string);
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            type="button"
                            role="checkbox"
                            aria-checked={isSelected}
                            aria-label={`${isSelected ? 'Remove' : 'Add'} ${pill.label}`}
                        >
                            {pill.label}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

// Action Buttons Component
const ActionButtons = ({ selectedOption, selectedPills, onCancel, onConfirm }: ActionButtonsProps) => {
    const isDisabled = !selectedOption && selectedPills.length === 0;

    return (
        <div className={styles.ActionButtons}>
            <button
                className={styles.BackButton}
                onClick={onCancel}
                onMouseDown={(e) => e.stopPropagation()}
                type="button"
                aria-label="Cancel selection"
            >
                Back
            </button>
            <button
                className={styles.ConfirmButton}
                onClick={onConfirm}
                onMouseDown={(e) => e.stopPropagation()}
                disabled={isDisabled}
                type="button"
                aria-label="Confirm selection"
                aria-disabled={isDisabled}
            >
                Save
            </button>
        </div>
    );
};

const MultiSelectSearchDropdown = (props: MultiSelectSearchDropdownProps) => {
    const {
        options,
        selectedItems,
        toggleSelection,
        placeholder,
        wrapperStyle,
        openAbove = false,
        isSingleSelect = false,
        onOptionClick,
        disabled = false,
        loading = false,
        inputKey,
        selectedItemStyle,
        selectedItemClassName,
        showFloatingLabel = true,
        showSelectAll = true,
        searchBarStyle = {},
        placeholderStyle = {},
        searchPosition = 'top',
        showRadioButtons = false,
        pillOptions,
        showActionButtons = false,
        onConfirm,
        onCancel,
        maxVisibleChips,
        sortSelectedOnTop = false,
        open,
        onOpenChange,
    } = props;
    const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [selectedOption, setSelectedOption] = useState<string | null>('');
    const [selectedPills, setSelectedPills] = useState<string[]>([]);
    const dropdownItemRef = useRef<HTMLDivElement>(null);
    const openOrderRef = useRef<{ label: string; value: string | null }[] | null>(null);
    const [filteredDropdownOptions, setFilteredDropdownOptions] = useState<
        | {
              label: string;
              value: string | null;
          }[]
        | null
    >(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const isAutoChips = maxVisibleChips === 'auto';
    const { chipRowRef, visibleChipCount: autoVisibleChips } = useAutoFitChips(isAutoChips, selectedItems.length);
    const visibleChipCount = isAutoChips ? autoVisibleChips : (maxVisibleChips as number | undefined);

    const isOpenControlled = open !== undefined;
    const showFilters = isOpenControlled ? open : uncontrolledOpen;
    const setShowFilters = useCallback(
        (nextOpen: boolean) => {
            if (!isOpenControlled) setUncontrolledOpen(nextOpen);
            onOpenChange?.(nextOpen);
        },
        [isOpenControlled, onOpenChange],
    );

    useEffect(() => {
        if (!showFilters) return;
        const closeOnOutsideMousedown = (event: MouseEvent) => {
            const box = dropdownRef.current;
            if (!box || box.contains(event.target as Node)) return;
            setShowFilters(false);
            setSearchText('');
            if (showActionButtons) {
                setSelectedOption('');
                setSelectedPills([]);
            }
        };
        document.addEventListener('mousedown', closeOnOutsideMousedown);
        return () => document.removeEventListener('mousedown', closeOnOutsideMousedown);
    }, [showFilters, setShowFilters, showActionButtons]);

    const handleSelection = (option: { label: string; value: string | null }) => {
        if (showActionButtons) {
            if (selectedOption === option.value) {
                setSelectedOption('');
                return;
            }
            setSelectedOption(option.value);
            return;
        }

        if (isSingleSelect) {
            // For single select, just call the callback
            onOptionClick?.(option);
            return;
        }

        if (inputKey === 'loanTypes') {
            if (selectedItems?.some((item) => item === option.label)) {
                toggleSelection?.(selectedItems.filter((item) => item !== option.label));
            } else {
                toggleSelection?.([...selectedItems, option.label]);
            }
            return;
        }
        // Multi-select behavior
        if (selectedItems?.some((item) => item === option.value)) {
            toggleSelection?.(selectedItems.filter((item) => item !== option.value));
        } else if (selectedItems?.some((item) => item === option.label)) {
            toggleSelection?.(selectedItems.filter((item) => item !== option.label));
        } else {
            toggleSelection?.([...selectedItems, option.value]);
        }
    };

    const handlePillToggle = useCallback((pillValue: string) => {
        setSelectedPills((prev) => (prev.includes(pillValue) ? prev.filter((p) => p !== pillValue) : [...prev, pillValue]));
    }, []);

    const handleConfirm = useCallback(() => {
        if (onConfirm && (selectedOption || selectedPills.length > 0)) {
            onConfirm(selectedOption ?? '', selectedPills);
            setShowFilters(false);
            setSelectedOption('');
            setSelectedPills([]);
            setSearchText('');
        }
    }, [onConfirm, selectedOption, selectedPills, setShowFilters]);

    const handleCancel = useCallback(() => {
        if (onCancel) {
            onCancel();
        }
        setShowFilters(false);
        setSelectedOption('');
        setSelectedPills([]);
        setSearchText('');
    }, [onCancel, setShowFilters]);

    const handleSelectAll = (e: React.MouseEvent | React.ChangeEvent) => {
        e.stopPropagation();

        if (!filteredDropdownOptions) return;

        const useLabel = inputKey === 'loanTypes';

        const filteredValues = filteredDropdownOptions.map((opt) => (useLabel ? opt.label : opt.value)) as (string | null)[];
        const selectedSet = new Set(selectedItems);

        const allSelected = filteredValues.every((value) => selectedSet.has(value));

        if (allSelected) {
            toggleSelection?.(selectedItems.filter((item) => !selectedSet.has(item) || !filteredValues.includes(item)));
        } else {
            const newSelection = Array.from(new Set([...selectedItems, ...filteredValues]));
            toggleSelection?.(newSelection);
        }
    };

    const handleClearAll = (e: React.MouseEvent) => {
        e.stopPropagation();
        toggleSelection?.([]);
    };

    const orderForOpen = () => {
        if (!sortSelectedOnTop) return options;
        const isSelected = (option: { label: string; value: string | null }) =>
            selectedItems?.some((item) => item === option.value || item === option.label);
        return [...options.filter(isSelected), ...options.filter((option) => !isSelected(option))];
    };

    useEffect(() => {
        const base = openOrderRef.current ?? options;
        if (searchText) {
            setFilteredDropdownOptions(base.filter((option) => option.label.toLowerCase().includes(searchText.toLowerCase())));
        } else {
            setFilteredDropdownOptions(base);
        }
    }, [searchText]);

    useEffect(() => {
        if (showFilters) {
            openOrderRef.current = orderForOpen();
            setFilteredDropdownOptions(openOrderRef.current);
            if (dropdownItemRef.current) {
                dropdownItemRef.current.scrollTo({ behavior: 'smooth', top: 0 });
            }
        }
    }, [showFilters]);

    return (
        <div
            ref={dropdownRef}
            style={{
                ...(selectedItems.length > 0
                    ? {
                          width: '100%',
                          background: 'white',
                          ...(showFilters ? {} : { border: '0.5px solid #c7c7c7' }),
                          ...wrapperStyle,
                          minHeight: '44px',
                          height: 'auto',
                      }
                    : { width: '100%', ...wrapperStyle, height: '44px' }),
                ...searchBarStyle,
                ...(disabled && { cursor: 'not-allowed' }),
                ...(isAutoChips ? { contain: 'inline-size' as const, paddingRight: '2.75rem' } : {}),
            }}
            onClick={() => {
                if (!loading && !disabled) {
                    setShowFilters(!showFilters);
                    setSearchText('');
                }
            }}
            className={`${styles.SearchBar} ${showFilters && !disabled ? styles.SearchBarActive : ''}`.trim()}
        >
            {selectedItems?.length > 0 ? (
                <>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '1ch',
                            flex: 1,
                            minHeight: '2.75rem',
                            padding: '0.5rem 0',
                            ...(isAutoChips ? { minWidth: 0 } : {}),
                        }}
                    >
                        {showFloatingLabel && <span className={styles.FloatingLabel}>{placeholder}</span>}
                        <div
                            ref={chipRowRef}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                minWidth: 0,
                                ...(isAutoChips
                                    ? { flex: '0 1 auto', gap: '0.25rem', flexWrap: 'nowrap' as const, overflow: 'hidden' }
                                    : { flex: 1, gap: '0.5ch', flexWrap: 'wrap' as const }),
                            }}
                        >
                            {(visibleChipCount ? selectedItems.slice(0, visibleChipCount) : selectedItems).map((item, index, visible) => {
                                const optionLabel =
                                    options.find((option) => option.value === item)?.label ||
                                    options.find((option) => option.label === item)?.label;
                                const isShockAbsorber = isAutoChips && index === visible.length - 1;
                                return (
                                    <span
                                        key={`${item}-${index}`}
                                        {...{ [CHIP_MEASURE_ATTR]: true }}
                                        style={{
                                            ...(selectedItemClassName
                                                ? {}
                                                : {
                                                      background: 'var(--secondary-bg-colour)',
                                                      color: 'var(--primary-text-colour)',
                                                  }),
                                            display: 'inline-block',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            maxWidth: '10rem',
                                            textOverflow: 'ellipsis',
                                            ...(isAutoChips && (isShockAbsorber ? { flexShrink: 1, minWidth: '4ch' } : { flexShrink: 0 })),
                                            ...selectedItemStyle,
                                        }}
                                        className={clsx(styles.SelectedItem, selectedItemClassName)}
                                    >
                                        {optionLabel}
                                    </span>
                                );
                            })}
                        </div>
                        {!!visibleChipCount && selectedItems.length > visibleChipCount && (
                            <span
                                className={styles.AndMore}
                                style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                                title={selectedItems
                                    .slice(visibleChipCount)
                                    .map(
                                        (item) =>
                                            options.find((option) => option.value === item)?.label ||
                                            options.find((option) => option.label === item)?.label,
                                    )
                                    .filter(Boolean)
                                    .join(', ')}
                            >
                                {`+ ${selectedItems.length - visibleChipCount} more`}
                            </span>
                        )}
                    </div>
                    <span className={`${styles.DownArrow} ${showFilters ? styles.DownArrowRotated : ''}`} style={{ alignSelf: 'center' }}>
                        <CaretIcon colour="#666" width="12" height="12" />
                    </span>
                </>
            ) : (
                <>
                    {loading ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                            <LoadingSpinner color="#6021b3" style={{ width: '16px', height: '16px', margin: '0px 0px 0px 0px' }} />
                        </div>
                    ) : (
                        <span
                            className={isSingleSelect ? styles.PlaceholderSingleSelectText : styles.PlaceholderText}
                            style={placeholderStyle}
                        >
                            {placeholder}
                        </span>
                    )}
                    {!loading && (
                        <span className={`${styles.DownArrow} ${showFilters ? styles.DownArrowRotated : ''}`}>
                            <CaretIcon colour="#666" width="12" height="12" />
                        </span>
                    )}
                </>
            )}

            <div
                ref={dropdownItemRef}
                className={`${styles.FilteredDropdownContainer} ${openAbove ? styles.OpenAbove : ''}`}
                style={{
                    ...(showFilters && !disabled
                        ? {
                              maxHeight: '550px', // Set a fixed max height
                              width: 'var(--dropdown-width, 100%)',
                              overflowY: 'hidden', // Hide overflow on container
                          }
                        : {
                              maxHeight: 0,
                              width: 'var(--dropdown-width, 100%)',
                          }),
                }}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
            >
                <div
                    style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}
                    className={styles.DropdownOptionsWrapper}
                >
                    {/* Render search at top when searchPosition is 'top' */}
                    {searchPosition === 'top' && (
                        <input
                            type="text"
                            placeholder="Search"
                            className={styles.SearchInputFilter}
                            value={searchText}
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                            onChange={(e) => setSearchText(e.target.value)}
                        />
                    )}
                    {showSelectAll && (
                        <div
                            className={styles.SelectAllWrapper}
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => handleSelectAll(e)}
                        >
                            <div className={styles.SelectAllCheckbox}>
                                <input
                                    type="checkbox"
                                    checked={
                                        filteredDropdownOptions?.every(
                                            (opt) => selectedItems.includes(opt.value) || selectedItems.includes(opt.label),
                                        ) && filteredDropdownOptions.length > 0
                                    }
                                    onChange={(e) => handleSelectAll(e)}
                                    onClick={(e) => e.stopPropagation()}
                                />
                                <span style={{ color: '#000000' }}>Select All</span>
                            </div>
                            <button className={styles.ClearAllButton} onClick={(e) => handleClearAll(e)}>
                                Clear All
                            </button>
                        </div>
                    )}
                    <div className={styles.FilteredDropdownWrapper} style={{ maxHeight: '450px', overflowY: 'auto' }}>
                        {filteredDropdownOptions?.length && filteredDropdownOptions?.length > 0 ? (
                            filteredDropdownOptions?.map((option, i) => {
                                const isSelected = showActionButtons
                                    ? selectedOption === option.value
                                    : selectedItems?.some((item) => item === option.value || item === option.label);

                                return (
                                    <div
                                        key={`${option.label}-${i}`}
                                        className={`${styles.FilteredDropdownItem} ${
                                            isSelected ? styles.FilteredDropdownItemSelected : ''
                                        }`}
                                        style={{ width: '100%', display: 'flex', overflow: 'hidden' }}
                                        onMouseDown={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            handleSelection(option);
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {isSingleSelect || showRadioButtons ? (
                                            <div className={styles.RadioButton} role="radio" aria-checked={isSelected}>
                                                {isSelected && <div className={styles.RadioButtonInner}></div>}
                                            </div>
                                        ) : (
                                            <input type="checkbox" checked={isSelected} readOnly style={{ flexShrink: 0 }} />
                                        )}
                                        <label
                                            className={styles.DropdownLabel}
                                            style={{
                                                flex: 1,
                                                minWidth: 0,
                                                overflowX: 'auto',
                                                textOverflow: 'clip',
                                                whiteSpace: 'nowrap',
                                                display: 'block',
                                                scrollbarWidth: 'none',
                                            }}
                                            title={option.label}
                                        >
                                            {option.label}
                                        </label>
                                    </div>
                                );
                            })
                        ) : (
                            <div className={styles.NoData}>
                                <span>No Results</span>
                            </div>
                        )}
                    </div>

                    {/* Pill Options Section */}
                    {pillOptions && pillOptions.length > 0 && (
                        <PillOptionsSection pillOptions={pillOptions} selectedPills={selectedPills} onPillToggle={handlePillToggle} />
                    )}

                    {/* Action Buttons Section */}
                    {showActionButtons && (
                        <ActionButtons
                            selectedOption={selectedOption}
                            selectedPills={selectedPills}
                            onCancel={handleCancel}
                            onConfirm={handleConfirm}
                        />
                    )}

                    {/* Render search at bottom when searchPosition is 'bottom' */}
                    {searchPosition === 'bottom' && (
                        <input
                            type="text"
                            placeholder="Search"
                            className={`${styles.SearchInputFilter} ${styles.SearchBottom}`}
                            value={searchText}
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                            onChange={(e) => setSearchText(e.target.value)}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default MultiSelectSearchDropdown;
